import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import ExtensionIcon from '@mui/icons-material/Extension';
import { AgenticFeatureRegistry, AgenticSettings, AgenticFeatureProvider } from './AgenticFeatureRegistry';

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
  onSettingsChange: (settings: AgenticSettings) => void;
}

const SettingsDialog: React.FC<SettingsDialogProps> = ({
  open,
  onClose,
  onSettingsChange,
}) => {
  const theme = useTheme();
  const [settings, setSettings] = useState<AgenticSettings>({
    agentId: '',
    applicationKey: '',
    assistantKey: '',
    featureKey: '',
  });
  const [providers, setProviders] = useState<AgenticFeatureProvider[]>([]);

  useEffect(() => {
    if (open) {
      // Load current settings
      const currentSettings = AgenticFeatureRegistry.getSettings();
      console.log('🔍 [SettingsDialog] Loading settings on dialog open:', currentSettings);
      setSettings(currentSettings);
      
      // Load registered providers
      const registeredProviders = AgenticFeatureRegistry.getAllProviders();
      setProviders(registeredProviders);
      
      // Subscribe to registry changes while dialog is open
      const unsubscribe = AgenticFeatureRegistry.subscribeToSettingsChanges((newSettings) => {
        console.log('🔔 [SettingsDialog] Registry settings changed, updating dialog:', newSettings);
        setSettings(newSettings);
      });
      
      // Cleanup subscription when dialog closes
      return () => {
        unsubscribe();
      };
    }
  }, [open]);

  const handleSettingChange = (field: keyof AgenticSettings, value: string) => {
    console.log('🔍 [SettingsDialog] Handling setting change:', field, value);
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
  };

  const handleSave = () => {
    // Update the registry
    AgenticFeatureRegistry.updateSettings(settings);
    
    // Notify parent component
    onSettingsChange(settings);
    
    onClose();
  };

  const handleCancel = () => {
    // Reset to original settings
    const currentSettings = AgenticFeatureRegistry.getSettings();
    setSettings(currentSettings);
    onClose();
  };

  const usePreferredAgentId = () => {
    console.log('🔍 [SettingsDialog] Using preferred agent ID');
    const preferredId = AgenticFeatureRegistry.getPreferredAgentId();
    console.log('🔍 [SettingsDialog] Preferred agent ID:', preferredId);
    if (preferredId) {
      handleSettingChange('agentId', preferredId);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SettingsIcon color="primary" />
          <Typography variant="h6">Chat Settings</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Assistant Configuration
          </Typography>
          
          <TextField
            fullWidth
            label="Agent ID"
            value={settings.agentId}
            onChange={(e) => handleSettingChange('agentId', e.target.value)}
            margin="normal"
            helperText="The unique identifier for the AI agent to use"
          />
          
          <TextField
            fullWidth
            label="Application Key"
            value={settings.applicationKey}
            onChange={(e) => handleSettingChange('applicationKey', e.target.value)}
            margin="normal"
            helperText="Application context key"
          />
          
          <TextField
            fullWidth
            label="Assistant Key"
            value={settings.assistantKey}
            onChange={(e) => handleSettingChange('assistantKey', e.target.value)}
            margin="normal"
            helperText="Assistant configuration key"
          />
          
          <TextField
            fullWidth
            label="Feature Key"
            value={settings.featureKey || ''}
            onChange={(e) => handleSettingChange('featureKey', e.target.value)}
            margin="normal"
            helperText="Feature-specific assistant (e.g., 'analytics-dashboard'). Leave blank for general assistant."
            placeholder="analytics-dashboard"
          />
          
          <TextField
            fullWidth
            label="Focus Key"
            value={settings.focusKey || ''}
            disabled
            margin="normal"
            helperText="Current entity focus (e.g., dashboard ID). Auto-set when editing specific items. This triggers automatic context loading."
            sx={{
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: theme.palette.text.secondary,
              }
            }}
          />

          {AgenticFeatureRegistry.getPreferredAgentId() && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={usePreferredAgentId}
                size="small"
              >
                Use Preferred Agent ID
              </Button>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Suggested by registered feature providers
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="h6" gutterBottom>
            Registered Feature Providers
          </Typography>
          
          {providers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No feature providers registered
            </Typography>
          ) : (
            <List dense>
              {providers.map((provider) => (
                <ListItem key={provider.id}>
                  <ListItemIcon>
                    <ExtensionIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={provider.name}
                    secondary={provider.id}
                  />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {provider.onAgentIdChange && (
                      <Chip label="Agent ID" size="small" variant="outlined" />
                    )}
                    {provider.onConversationClear && (
                      <Chip label="Clear Conv" size="small" variant="outlined" />
                    )}
                    {provider.onPromptSent && (
                      <Chip label="Prompt" size="small" variant="outlined" />
                    )}
                    {provider.onRunCompleted && (
                      <Chip label="Run Complete" size="small" variant="outlined" />
                    )}
                    {provider.onFocusChange && (
                      <Chip label="Focus" size="small" variant="outlined" />
                    )}
                    {provider.getPromptForFocus && (
                      <Chip label="Auto-Context" size="small" variant="outlined" color="primary" />
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsDialog; 